/* eslint-disable */

import { Circle, Style, Fill, Stroke, RegularShape, Text } from 'ol/style';
import { Circle as CircleGeometry, MultiPoint, LineString, Point, Polygon } from 'ol/geom';
import { createDefaultStyle, createEditingStyle } from 'ol/style/Style';
import GeometryType from '../GeometryType';
import { extend } from 'ol/array';
import GeometryCollectionTextStyle from './GeometryCollectionTextStyle';

import ImageOverlay from './ImageOverlay';

export const TRANSPARENT = createStyleCirclePoint('transparent', 'transparent');
export const BUFFER_AREA = createStyleCirclePoint('rgba(0, 0, 0, 0)', 'black');
export const FEATURE_INFO = createStyleCirclePoint('rgba(175, 203, 56, 0.5)', '#373736');
export const SELECTED_FEATURE = createStyleCirclePointSelectedFeature('rgba(175, 203, 56, 0.5)', '#afcb38');
export const SELECTED_FEATURE_POINT = {
  DAY: createStyleCirclePointSelectedFeaturePoint('rgba(175, 203, 56, 0.5)', '#afcb38'),
  DUSK: createStyleCirclePointSelectedFeaturePoint('rgba(22, 73, 25, 0.5)', '#164919'),
  NIGHT: createStyleCirclePointSelectedFeaturePoint('rgba(6, 34, 8, 0.5)', '#062208')
}
export const DRAWN_FEATURE = createStyleCirclePoint('rgba(175, 203, 56, 0.5)', '#afcb38', 0);
export const DRAW_FEATURE = [DRAWN_FEATURE, createStyleShowEdge('orange', 'orange')];

export const ALERT_INFO = createStyleSquarePoint('rgba(255, 255, 0, 0.5)', 'yellow');
export const ALERT_WARNING = createStyleSquarePoint('rgba(255, 165, 0, 0.1)', 'orange');
export const ALERT_ALARM = createStyleSquarePoint('rgba(255, 0, 0, 0.1)', 'red');

export const DEFAULT_SHIP = createDefaultShipStyle;
export const SELECTED_SHIP = createSelectedShipStyle;
export const TRANSLATED_SHIP = createTranslatedShipStyle();
export const BCM_SHIP = new Style({
  stroke: createStrokeStyle('black', 2, [10, 10])
});
export const BG_SHIP = createBgShipStyle;

export const VESSEL_SHIP_ROUTE = createVesselShipRouteStyle();
export const TRAJECTORY_SHIP_ROUTE = new Style({
  stroke: createStrokeStyle('black', 2, [10, 10])
});

const graticuleTextFont = '12px';

export const GRATICULE_GRID_STROKE_DAY = toStyle(createStrokeStyle([95, 106, 96], 1));
export const GRATICULE_GRID_STROKE_DUSK = toStyle(createStrokeStyle([124, 138, 127], 1));
export const GRATICULE_GRID_STROKE_NIGHT = toStyle(createStrokeStyle([57, 63, 57], 1));

export const GRATICULE_TEXT_LON_DAY = toStyle(createTextStyle({font: graticuleTextFont, textBaseline: 'bottom', offsetY : -100}, [0, 0, 0], [216, 244, 225]));
export const GRATICULE_TEXT_LON_DUSK = toStyle(createTextStyle({font: graticuleTextFont, textBaseline: 'bottom', offsetY : -100}, [124, 138, 127], [157, 176, 161]));
export const GRATICULE_TEXT_LON_NIGHT = toStyle(createTextStyle({font: graticuleTextFont, textBaseline: 'bottom', offsetY : -100}, [57, 63, 57], [73, 81, 74]));

export const GRATICULE_TEXT_LAT_DAY = toStyle(createTextStyle({font: graticuleTextFont, textAlign: 'end', offsetX : -60}, [0, 0, 0], [216, 244, 225]));
export const GRATICULE_TEXT_LAT_DUSK = toStyle(createTextStyle({font: graticuleTextFont, textAlign: 'end', offsetX : -60}, [124, 138, 127], [157, 176, 161]));
export const GRATICULE_TEXT_LAT_NIGHT = toStyle(createTextStyle({font: graticuleTextFont, textAlign: 'end', offsetX : -60}, [57, 63, 57], [73, 81, 74]));

/** @type {function(WayPointTextStyleCondition,string,string):Style} */
export const ROUTE_WAY_POINT = (wayPointTextStyleCondition, textColor, circleColor) => createStyleRoutePoints(wayPointTextStyleCondition, textColor, circleColor, 15);
/** @type {function(string):Style} */
export const ROUTE_LINE_STRING = lineColor => new Style({stroke: createStrokeStyle(lineColor, 2)});
export const MEASURE = measureColor => [
  createStyleCirclePoint('rgba(0, 0, 0, 0)', measureColor, 5, [10,10]),
  createStyleShowEdge(setAlphaOnColor(measureColor, 0.5), measureColor)
];

/**
 * @typedef {Object} WayPointLabelTextStyle
 * @property {string} font The text font.
 * @property {string} text The text.
 */

/**
 * @typedef {function(import('ol').Feature):WayPointLabelTextStyle} WayPointTextStyleCondition
 */

/**
 * Create route way point style.
 * @param {WayPointTextStyleCondition} wayPointTextStyleCondition The function to set what to display.
 * @param {import("ol/color").Color|string} textColor Way point text color
 * @param {import("ol/color").Color|string} circleColor Way point circle color
 * @param {number} [radius] way point circle size. Default 20.
 * @return {StyleFunction} The route style.
 */
function createStyleRoutePoints(wayPointTextStyleCondition, textColor, circleColor,  radius = 20) {
  const fill = new Fill({
    color: circleColor
  });
  const style =  new Style({
    image: new Circle({
      radius: radius, 
      fill: fill,
      stroke: createStrokeStyle(textColor, 2)
    })
  });

  return function(feature, resolution) {
    if (!feature.getGeometry()) {
      return null;
    }
    const textOptions = {
      textAlign: 'center',
      textBaseline: 'middle',
      fill: new Fill({color: textColor})
    };
    let textStyle = wayPointTextStyleCondition(feature);
    if(!textStyle) {
      return null;
    } 
    style.setText(new Text({
      ...textOptions,
      ...textStyle
    }));
    return style;
  };
}

/**
 * Create style with circle point representation.
 * @param {string} fillColor The fill color.
 * @param {string} strokeColor The stroke color.
 * @param {number} radius The radius of the circle for a circle point representation.
 * @param {Array<number>} lineDash The dash line 
 * @return {Style} The created style.
 */
function createStyleCirclePoint(fillColor, strokeColor, radius, lineDash) {
  let fill = new Fill({
    color: fillColor
  });
  const styleOptions = {
    fill: fill,
    stroke: createStrokeStyle(strokeColor, 3, lineDash)
  };
  if(radius > 0) {
    styleOptions.image = new Circle({
      radius: radius, 
      fill: fill,
      stroke: createStrokeStyle(strokeColor, 3)
    });
  }
  return new Style(styleOptions);
}

function createStyleCirclePointSelectedFeature(fillColor, strokeColor, radius = 0.2, lineDash) {
  let fill = new Fill({
    color: fillColor
  });
  const styleOptions = {
    fill: fill,
    stroke: createStrokeStyle(strokeColor, 1, lineDash)
  };

  if(radius > 0) {
    styleOptions.image = new Circle({
      radius: radius, 
      fill: fill,
      stroke: createStrokeStyle(strokeColor, 3)
    });
  }
  return new Style(styleOptions);
}

function createStyleCirclePointSelectedFeaturePoint(fillColor, strokeColor, radius = 24, lineDash) {
  let fill = new Fill({
    color: fillColor
  });
  const styleOptions = {
    fill: fill,
    stroke: createStrokeStyle(strokeColor, 1, lineDash)
  };

  if(radius > 0) {
    styleOptions.image = new Circle({
      radius: radius, 
      fill: fill,
      stroke: createStrokeStyle(strokeColor, 3)
    });
  }
  return new Style(styleOptions);
}

/**
 * Create style with square point representation.
 * @param {string} fillColor Fill color.
 * @param {string} strokeColor Stroke color.
 * @param {number} radius The square size.
 * @return {Style} The created style.
 */
function createStyleSquarePoint(fillColor, strokeColor, radius = 12) {
  let fill = new Fill({
    color: fillColor
  });
  const stroke = createStrokeStyle(strokeColor, 3);
  return new Style({
    fill: fill,
    stroke: stroke,
    image: new RegularShape({
      points: 4, 
      radius: radius, 
      angle: Math.PI / 4, 
      fill: fill,
      stroke: stroke
    })
  });
}

/**
 * Create style which display edge in feature.
 * @param {string} fillColor The fill color.
 * @param {string} strokeColor The stroke color.
 * @return {Style} The created style.
 */
function createStyleShowEdge(fillColor, strokeColor) {
  return new Style({
    image: new Circle({
      radius: 5, 
      fill: new Fill({
        color: fillColor
      }),
      stroke: createStrokeStyle(strokeColor, 3)
    }),
    geometry: function(feature) {
      let coordinates, geometry = feature.getGeometry();
      if(geometry instanceof LineString) {
        coordinates = geometry.getCoordinates();
      } else if(geometry instanceof Point) {
        coordinates = [geometry.getCoordinates()];
      } else if(geometry instanceof Polygon) {
        coordinates = geometry.getCoordinates()[0];
      } else if(geometry instanceof CircleGeometry) {
        coordinates = [geometry.getCenter()];
      }
      // return the coordinates of the first ring of the polygon
      return new MultiPoint(coordinates || []);
    }
  });
}

/**
 * Create a ship style with text.
 * @return {StyleFunction} The style function to styilish feature.
 */
function createDefaultShipStyle(map) {
  const defaultStyle  = createDefaultStyle()[0]
  const firstColor = defaultStyle.getStroke().getColor()
  const highlightColor = 'white'

  return function(feature, resolution) {
    const bgFile = feature.getBgFile()
    
    if (!feature.getGeometry()) {
      return null
    }

    const newStyle = new Style({
      stroke: new Stroke({
        color: defaultStyle.getStroke().getColor(),
        width: defaultStyle.getStroke().getWidth()
      }),
      fill: new Fill({
        color: 'rgba(255,255,255,0.4)'
      })
    })

    if (bgFile) {
      createBgShipStyle(map)(feature)
      const transparentStyle = new Style({
        stroke: new Stroke({
          color: '#3399CC00' // Totalement transparent
        }),
        fill: new Fill({
          color: '#FFFFFF00' // Totalement transparent
        })
      })
      return [transparentStyle];
    } else {
      const textStyle = new GeometryCollectionTextStyle(createShipTextStyle(feature, firstColor, highlightColor))
      return [newStyle, textStyle]
    }
  };
}

function createDefaultStyleForShip(feature) {
  const style = createDefaultStyle()[0];

  const firstColor = style.getStroke().getColor();
  const highlightColor = 'white';

  if (!feature.getGeometry()) {
    return null;
  }

  const textStyle = new GeometryCollectionTextStyle(createShipTextStyle(feature, firstColor, highlightColor));
  return [style, textStyle];
}

function createBgShipStyle(map, selected = false, styles = null) {
  return function(feature) {
    const bgFile = feature.getBgFile()
    const imgOverlay = feature.get('imageOverlay')

    let style
    if (selected && styles) {
      const firstColor = styles[GeometryType.LINE_STRING][1].getStroke().getColor();
      const highlightColor = styles[GeometryType.LINE_STRING][0].getStroke().getColor();
    
      if(feature.getGeometry().getType() === GeometryType.GEOMETRY_COLLECTION) {
        const geometryCollectionTextStyleIndex = styles[GeometryType.GEOMETRY_COLLECTION].findIndex(style => {
          return style instanceof GeometryCollectionTextStyle;
        });
              
        const textStyle = createShipTextStyle(feature, firstColor, highlightColor);
        if(geometryCollectionTextStyleIndex === -1) {
          styles[GeometryType.GEOMETRY_COLLECTION].map(style => style.setText(null));
          styles[GeometryType.GEOMETRY_COLLECTION].push(new GeometryCollectionTextStyle(textStyle));
        } else {
          styles[GeometryType.GEOMETRY_COLLECTION][geometryCollectionTextStyleIndex].setText(textStyle);
        }
      }
      style = styles[feature.getGeometry().getType()];
    }
    else {
      style = createDefaultStyleForShip(feature)
    }

    if (bgFile) {
      const geometry = feature.getGeometry()
      const rotation = feature.getOrientation()
      const shipLength = feature.get('length')
      const shipWidth = feature.get('width')
      const lengthToUse = Math.max(shipLength, shipWidth)
      if (!imgOverlay) {
        const newImgOverlay = createImageOverlay(map, feature.getId(), bgFile, geometry, rotation, lengthToUse)
        feature.set('imageOverlay', newImgOverlay)
      } else {
        imgOverlay.updateGeometry(geometry, rotation)
      }
    }
    return style
  }
}

function createImageOverlay(map, id, imageUrl, geometry, rotation, lengthToUse) {
  const checkGeometry = () => {
    if (!geometry) {
      requestAnimationFrame(checkGeometry)
      return
    }
    return new ImageOverlay({
      id,
      map,
      imageUrl,
      geometry,
      rotation,
      lengthToUse
    })
  }

  return checkGeometry()
}

/**
 * Create a selected ship style with text.
 * @return {StyleFunction} The style function to styilish feature.
 */
function createSelectedShipStyle(map) {
  const styles = createEditingStyle();
  extend(styles[GeometryType.POLYGON], styles[GeometryType.LINE_STRING]);
  extend(styles[GeometryType.GEOMETRY_COLLECTION], styles[GeometryType.LINE_STRING]);

  const firstColor = styles[GeometryType.LINE_STRING][1].getStroke().getColor();
  const highlightColor = styles[GeometryType.LINE_STRING][0].getStroke().getColor();

  return function(feature, resolution) {
    if (feature.getBgFile()) return createBgShipStyle(map, true, styles)(feature)
    if (!feature.getGeometry()) {
      return null;
    }
    if(feature.getGeometry().getType() === GeometryType.GEOMETRY_COLLECTION) {
      const geometryCollectionTextStyleIndex = styles[GeometryType.GEOMETRY_COLLECTION].findIndex(style => {
        return style instanceof GeometryCollectionTextStyle;
      });
            
      const textStyle = createShipTextStyle(feature, firstColor, highlightColor);
      if(geometryCollectionTextStyleIndex === -1) {
        styles[GeometryType.GEOMETRY_COLLECTION].map(style => style.setText(null));
        styles[GeometryType.GEOMETRY_COLLECTION].push(new GeometryCollectionTextStyle(textStyle));
      } else {
        styles[GeometryType.GEOMETRY_COLLECTION][geometryCollectionTextStyleIndex].setText(textStyle);
      }
    }
    return styles[feature.getGeometry().getType()];
  };
}

/**
 * Create a translated ship style without text.
 * @return {StyleFunction} The style function to styilish feature.
 */
function createTranslatedShipStyle() {
  const styles = createEditingStyle();
  return function(feature, resolution) {
    return styles[feature.getGeometry().getType()];
  };
}

/**
 * Create a ship style with text.
 * @return {StyleFunction} The style function to styilish feature.
 */
function createVesselShipRouteStyle() {
  return createDefaultShipStyle();
}

/**
 * Create a text style for {@link Ship} feature.
 * @param {import("ol/Feature").default} feature The feature to stylish.
 * @param {import("ol/color").Color|string} firstColor The text color.
 * @param {import("ol/color").Color|string} [highlightColor] The highlight color to help text visibility.
 * @return {Text} The text style.
 */
function createShipTextStyle(feature, firstColor, highlightColor) {
  return createTextStyle({font: 'bold 12px Arial', text: feature.get('label')}, firstColor, highlightColor);
}

/**
 * Create a text style.
 * @param {import("ol/style/Text").Options} textOptions The text color.
 * @param {import("ol/color").Color|string} textColor The text color.
 * @param {import("ol/color").Color|string} glowColor The glow text color.
 */
function createTextStyle(textOptions, textColor, glowColor) {
  textOptions.stroke = glowColor ? createStrokeStyle(glowColor, 3) : undefined;
  textOptions.fill = new Fill({
    color : textColor
  });
    
  return new Text(textOptions);
}

/**
 * Create a stroke line.
 * @param {import("ol/color").Color|string} color The stroke color.
 * @param {number} [width] The stroke line color.
 * @param {Array<number>} [lineDash] The dash line.
 */
function createStrokeStyle(color, width = 1, lineDash) {
  return new Stroke({color, lineDash, width});
}

/**
 * Transform text, stroke or fill style to style.
 * @param {import('ol/style').Style} style The sub style object to parse.
 * @return {import('ol/style/Style').default} A style that contains sub stykle
 */
function toStyle(style) {
  let newStyleOption;
  if(style instanceof Text) {
    newStyleOption = {text: style};
  } else if(style instanceof Stroke) {
    newStyleOption = {stroke: style};
  } else if(style instanceof Fill) {
    newStyleOption = {fill: style};
  }
  return new Style(newStyleOption);
}

/**
 * Add alpha on hexa or rgb color.
 * @param {string} color The color like.
 * @param {number} alpha The alpha to set (between 0 and 1).
 * @return {string} The new color like of initial color if color is not hexa or rgb color
 */
function setAlphaOnColor(color, alpha) {
  let result = getHexaValues(color);
  if(!result) {
    result = getRGBValues(color);
  }
    
  let newColor;
  if(result) {
    newColor = `rgba(${result[0]},${result[1]},${result[2]},${alpha})`;
  } else {
    newColor = color;
  }
  return newColor;
}

/**
 * Get the rgb values from rgb|rgba color.
 * @param {string} color The color like
 * @return {Array<number>} The RGB array or null if not an rgb|rgba color.
 */
function getRGBValues(color) {
  const result = /rgba?\((\d+),\s*(\d+),\s*(\d+)(,\s*(1|0?\.\d+))?\)/i.exec(color);
  return result ? [
    parseInt(result[1]),
    parseInt(result[2]),
    parseInt(result[3])
  ] : null;
}

/**
 * Get the rgb values from hexa color.
 * @param {string} color The color like
 * @return {Array<number>} The RGB array or null if not an hexa color.
 */
function getHexaValues(color) {  
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(color);
  return result ? [
    parseInt(result[1], 16),
    parseInt(result[2], 16),
    parseInt(result[3], 16)
  ] : null;
}